import {
    Coroutine,
    CoroutineManager,
    waitForCondition,
    wlUtils,
} from '@sorskoot/wonderland-components';
import { InputComponent, Object3D } from '@wonderlandengine/api';
import { property } from '@wonderlandengine/api/decorators.js';

import { Cursor } from '@wonderlandengine/components';
import { TextsManager } from '../core/texts-manager.js';
import { GlobalEvents } from '../core/global-events.js';
import { InteractableBase } from './interactable-base.js';
import { GameManager, ObjectiveType } from '../core/game-manager.js';
import { ObjectivesManager } from '../core/objectives-manager.js';

export enum ItemType {
    DescriptionOnly = 0, // only show a text when interacting with the item
    Inspect = 1, // Move item to your hand to view it, then place it back
    Carry = 2, // Move the item to your hand and then place it in your inventory
}

export class Interactable extends InteractableBase {
    static TypeName = 'interactable';
    static InheritProperties = true;

    @property.enum(Object.keys(ItemType).filter((e) => isNaN(Number(e))))
    itemType: ItemType = ItemType.DescriptionOnly;

    @property.string('') // The text to show when interacting with the item
    description: string = '';

    @property.object({ required: true })
    descriptionTarget!: Object3D;

    // objective to complete when interacting with this item
    @property.enum(
        Object.keys(ObjectiveType).filter((e) => isNaN(Number(e))),
        ObjectiveType.None
    )
    objective: ObjectiveType = ObjectiveType.None;

    private _coroutineManager: CoroutineManager = new CoroutineManager();
    private _originalTransform = new Float32Array(8);
    private _originalParent: Object3D | null = null;

    /**
     * Updates the component and the coroutine manager.
     * @param dt - Delta time since the last frame in seconds
     */
    update(dt: number) {
        super.update(dt);
        this._coroutineManager.update(dt);
    }

    /**
     * Called when the component is activated. Stores the original transform
     * and parent of the object for later restoration.
     */
    onActivate(): void {
        super.onActivate();
        this.object.getTransformWorld(this._originalTransform);
        this._originalParent = this.object.parent;
    }

    /**
     * Handles interaction with the object. Shows description text if available
     * and starts inspection/carry behavior based on itemType.
     * @param cursor - The cursor that triggered the interaction
     * @override
     */
    override interact(cursor: Cursor) {
        if (this.description !== '') {
            TextsManager.instance.showText(
                this.description,
                this.descriptionTarget
            );
        }
        if (this.itemType == ItemType.DescriptionOnly) {
            return;
        }

        // Handle regular cursor down
        this._coroutineManager.stopCoroutine(this._inspectId);
        this._coroutineManager.addCoroutine(
            new Coroutine(this._inspect(cursor.object))
        );
    }

    private _inspectId = -1;

    /**
     * Coroutine that handles the inspection and carrying behavior of the item.
     * Attaches the item to the hand, waits for release, then either adds to
     * inventory (if Carry type) or returns to original position (if Inspect type).
     * @param target - The hand object to attach the item to
     * @yields Control back to the coroutine manager
     */
    private *_inspect(target: Object3D) {
        const inputComponent = target.getComponent(InputComponent)!;
        if (inputComponent.handedness == null) {
            return; // not sure how this can happen, so ignore.
        }

        this.object.parent = target;
        this._physxObject.active = false;

        GlobalEvents.instance.interactableGrabbed.dispatch(
            inputComponent.handedness,
            this.object
        );

        //TODO: Add a lerp on positions.

        yield* waitForCondition(() => {
            return !inputComponent.xrInputSource?.gamepad?.buttons[0].pressed;
        });

        if (this.objective !== ObjectiveType.None) {
            GameManager.instance.completeObjective(this.objective);
        }

        if (this.itemType == ItemType.Carry) {
            // TODO: add the object to inventory
            GlobalEvents.instance.interactableReleased.dispatch(
                inputComponent.handedness,
                this.object
            );

            // How can I add it to the objectives if needed?
            TextsManager.instance.showCaption(`I'll take this with me.`);
            yield;
            // for now, just destroy the object
            //this.object.parent = this._originalParent;
            setTimeout(() => this.object.destroy());
            // this.object.parent = this._originalParent;
            // this.object.setTransformWorld(this._originalTransform);
            // this._physxObject.active = true;
            return;
        }
        //TODO: Add a lerp back to original position.

        GlobalEvents.instance.interactableReleased.dispatch(
            inputComponent.handedness,
            this.object
        );
        this.object.parent = this._originalParent;
        this.object.setTransformWorld(this._originalTransform);
        this._physxObject.active = true;
    }
}
